import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { sendNotification } from "../utils/sendNotification";

const DOCUMENTS_STORAGE_KEY = "yoma_documents";
const PROJECTS_STORAGE_KEY = "yoma_projects";

const OPC_HIDDEN_DOC_TYPES = ["Devis", "Facture"];

export default function DocumentsPage({ isOPCMandate = false, cabinetId = null }) {
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [filterType, setFilterType] = useState("tous");
  const [filterProject, setFilterProject] = useState("tous");
  const [search, setSearch] = useState("");

  const [form, setForm] = useState({
    projectId: "",
    projectName: "",
    clientNom: "",
    typeDocument: "Compte rendu",
    titre: "",
    contenu: "",
  });

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      if (cabinetId == null) return;
      try {
        const { data: documentsData, error: documentsError } = await supabase
          .from("documents")
          .select("*")
          .eq("cabinet_id", cabinetId)
          .order("id", { ascending: false });
        if (documentsError) throw documentsError;

        const { data: projectsData, error: projectsError } = await supabase
          .from("projects")
          .select("id, name, client_name")
          .eq("cabinet_id", cabinetId)
          .order("created_at", { ascending: false });
        if (projectsError) throw projectsError;

        if (!cancelled) {
          const normalizedProjects = (projectsData || []).map((p) => ({
            ...p,
            nomProjet: p.name || "",
            clientNom: p.client_name || "",
          }));
          setDocuments(documentsData || []);
          setProjects(normalizedProjects);
        }
      } catch (error) {
        console.error("Erreur chargement documents:", error);
      } finally {
        if (!cancelled) setIsLoaded(true);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [cabinetId]);

  useEffect(() => {
    if (!isLoaded) return;

    try {
      localStorage.setItem(DOCUMENTS_STORAGE_KEY, JSON.stringify(documents));
    } catch (error) {
      console.error("Erreur sauvegarde documents:", error);
    }
  }, [documents, isLoaded]);

  useEffect(() => {
    if (!isOPCMandate) return;
    setForm((prev) => {
      if (OPC_HIDDEN_DOC_TYPES.includes(prev.typeDocument)) {
        return { ...prev, typeDocument: "Compte rendu" };
      }
      return prev;
    });
    setFilterType((ft) => (OPC_HIDDEN_DOC_TYPES.includes(ft) ? "tous" : ft));
  }, [isOPCMandate]);

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function handleProjectChange(projectId) {
    const project = projects.find((p) => String(p.id) === String(projectId));

    setForm((prev) => ({
      ...prev,
      projectId,
      projectName: project ? project.nomProjet || "" : "",
      clientNom: project ? project.clientNom || "" : "",
    }));
  }

  async function ajouterDocument() {
    if (!form.projectId || !form.typeDocument || !form.titre) return;

    const titre_document = form.titre.trim();
    const nom_projet = form.projectName || "—";
    const project_id = form.projectId;

    const nouveauDocument = {
      ...form,
      project_id: form.projectId,
      projectName: form.projectName || "—",
      clientNom: form.clientNom || "—",
      dateCreation: new Date().toLocaleDateString("fr-FR"),
      cabinet_id: cabinetId,
    };

    const { data: insertedDoc, error: insertError } = await supabase
      .from("documents")
      .insert([nouveauDocument])
      .select()
      .single();
    if (insertError) {
      console.error("Erreur insertion document:", insertError);
      alert("Erreur lors de l'ajout du document.");
      return;
    }

    setDocuments((prev) => [insertedDoc, ...prev]);

    setForm({
      projectId: "",
      projectName: "",
      clientNom: "",
      typeDocument: "Compte rendu",
      titre: "",
      contenu: "",
    });

    const { data: authData } = await supabase.auth.getUser();
    const currentUser = authData?.user;
    if (currentUser?.id) {
      try {
        await sendNotification({
          supabase,
          destinataireId: currentUser.id,
          expediteurId: currentUser.id,
          type: "document",
          titre: "Nouveau document partagé",
          message: `Un nouveau document "${titre_document}" a été ajouté au projet ${nom_projet}.`,
          projetId: project_id,
          sendEmail: false,
        });
      } catch (e) {
        console.error("sendNotification:", e);
      }
    }
  }

  function supprimerDocument(id) {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  }

  function getTypeStyle(type) {
    if (type === "Compte rendu") return { color: "#2980B9", fontWeight: 700 };
    if (type === "Ordre de service") return { color: "#D68910", fontWeight: 700 };
    if (type === "Mise en demeure") return { color: "#C0392B", fontWeight: 700 };
    if (type === "Annexe") return { color: "#1E8449", fontWeight: 700 };
    return { color: "#555", fontWeight: 700 };
  }

  function getTypeBadgeStyle(type) {
    if (type === "Compte rendu") {
      return { color: "#C09A5B", background: "rgba(192,154,91,0.14)", border: "1px solid rgba(192,154,91,0.4)" };
    }
    if (type === "Ordre de service") {
      return { color: "#8B7361", background: "rgba(139,115,97,0.14)", border: "1px solid rgba(139,115,97,0.4)" };
    }
    if (type === "Mise en demeure") {
      return { color: "#B85C3A", background: "rgba(184,92,58,0.14)", border: "1px solid rgba(184,92,58,0.4)" };
    }
    if (type === "Annexe") {
      return { color: "#5C8A5C", background: "rgba(92,138,92,0.14)", border: "1px solid rgba(92,138,92,0.4)" };
    }
    return { color: "#3D2E20", background: "rgba(61,46,32,0.08)", border: "1px solid rgba(61,46,32,0.25)" };
  }

  const visibleDocuments = isOPCMandate
    ? documents.filter((d) => !OPC_HIDDEN_DOC_TYPES.includes(d.typeDocument))
    : documents;

  const totalDocs = visibleDocuments.length;
  const totalCR = visibleDocuments.filter((d) => d.typeDocument === "Compte rendu").length;
  const totalOS = visibleDocuments.filter((d) => d.typeDocument === "Ordre de service").length;
  const totalMD = visibleDocuments.filter((d) => d.typeDocument === "Mise en demeure").length;

  const documentsFiltres = visibleDocuments.filter((doc) => {
    const okType = filterType === "tous" || doc.typeDocument === filterType;
    const okProject = filterProject === "tous" || String(doc.projectId) === String(filterProject);
    const haystack = `${doc.titre || ""} ${doc.contenu || ""} ${doc.projectName || ""} ${doc.clientNom || ""}`.toLowerCase();
    const okSearch = !search.trim() || haystack.includes(search.toLowerCase().trim());
    return okType && okProject && okSearch;
  });

  if (cabinetId == null) {
    return (
      <main style={{ minHeight: "100vh", background: "#F8F4EE", padding: 24 }}>
        <div style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", color: "#8B7361" }}>
          Chargement...
        </div>
      </main>
    );
  }

  return (
    <main style={{ minHeight: "100vh", background: "#F8F4EE", padding: 24 }}><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;700&display=swap');
      `}</style><header style={{ marginBottom: 16 }}><h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 32 }}>Documents</h1><p style={{ marginTop: 6, fontFamily: "'DM Sans', sans-serif", color: "#8B7361", fontSize: 13 }}>Gestion documentaire YOMA</p></header><section style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: 16 }}><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8B7361", fontSize: 11, marginBottom: 7 }}>Total documents</div><div style={{ fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 28, fontWeight: 700 }}>{totalDocs}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(192,154,91,.35)", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8B7361", fontSize: 11, marginBottom: 7 }}>Comptes rendus</div><div style={{ fontFamily: "'Playfair Display', serif", color: "#C09A5B", fontSize: 28, fontWeight: 700 }}>{totalCR}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(139,115,97,.35)", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8B7361", fontSize: 11, marginBottom: 7 }}>Ordres de service</div><div style={{ fontFamily: "'Playfair Display', serif", color: "#8B7361", fontSize: 28, fontWeight: 700 }}>{totalOS}</div></div><div style={{ background: "#FFFFFF", border: "1px solid rgba(184,92,58,.35)", borderRadius: 12, padding: 12 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8B7361", fontSize: 11, marginBottom: 7 }}>Mises en demeure</div><div style={{ fontFamily: "'Playfair Display', serif", color: "#B85C3A", fontSize: 28, fontWeight: 700 }}>{totalMD}</div></div></section><section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 16, marginBottom: 16 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}><h2 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 26 }}>Nouveau document</h2><button
            onClick={ajouterDocument}
            style={{ border: "none", background: "#C09A5B", color: "#FFFFFF", borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700 }}
          >
            Ajouter
          </button></div><div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 12 }}><div><label style={{ display: "block", marginBottom: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361" }}>Projet</label><select value={form.projectId} onChange={(e) => handleProjectChange(e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif" }}><option value="">Sélectionner un projet</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.nomProjet} — {project.clientNom}
                </option>
              ))}
            </select></div><div><label style={{ display: "block", marginBottom: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361" }}>Type de document</label><select value={form.typeDocument} onChange={(e) => updateField("typeDocument", e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif" }}><option value="Compte rendu">Compte rendu</option><option value="Ordre de service">Ordre de service</option><option value="Mise en demeure">Mise en demeure</option><option value="Note interne">Note interne</option><option value="Annexe">Annexe</option></select></div><div><label style={{ display: "block", marginBottom: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361" }}>Titre</label><input placeholder="Titre du document" value={form.titre} onChange={(e) => updateField("titre", e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif" }} /></div><div><label style={{ display: "block", marginBottom: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8B7361" }}>Contenu</label><textarea placeholder="Contenu du document" rows="3" value={form.contenu} onChange={(e) => updateField("contenu", e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", resize: "vertical" }} /></div></div></section><section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 16 }}><div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}><h3 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 24 }}>Historique des documents</h3><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ border: "1px solid #D8C9B8", borderRadius: 8, padding: "8px 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}><option value="tous">Tous les types</option><option value="Compte rendu">Compte rendu</option><option value="Ordre de service">Ordre de service</option><option value="Mise en demeure">Mise en demeure</option><option value="Note interne">Note interne</option><option value="Annexe">Annexe</option></select><select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} style={{ border: "1px solid #D8C9B8", borderRadius: 8, padding: "8px 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 12 }}><option value="tous">Tous les projets</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.nomProjet}
                </option>
              ))}
            </select><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher..." style={{ border: "1px solid #D8C9B8", borderRadius: 8, padding: "8px 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, minWidth: 180 }} /></div></div>

        {documentsFiltres.length === 0 ? (
          <div style={{ textAlign: "center", padding: "30px 20px" }}><div style={{ fontSize: 30, marginBottom: 8 }}>◈</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#3D2E20", marginBottom: 6 }}>Aucun document</div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#8B7361" }}>
              Aucun document ne correspond aux filtres actuels.
            </div></div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {documentsFiltres.map((doc) => (
              <article key={doc.id} style={{ border: "1px solid #E9DDCF", borderRadius: 10, padding: 12, background: "#FDFBF8" }}><div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}><div style={{ flex: 1 }}><span style={{ display: "inline-block", borderRadius: 999, padding: "4px 10px", marginBottom: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", ...getTypeBadgeStyle(doc.typeDocument) }}>
                      {doc.typeDocument}
                    </span><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, fontWeight: 700, color: "#3D2E20", marginBottom: 4 }}>{doc.titre || "Sans titre"}</div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8B7361", marginBottom: 5 }}>
                      {doc.projectName || "Projet non défini"} · {doc.clientNom || "Client non défini"}
                    </div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8B7361", marginBottom: 8 }}>
                      Créé le {doc.dateCreation || "—"}
                    </div><div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#3D2E20", lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {doc.contenu || "Aucun contenu"}
                    </div></div><div style={{ display: "flex", gap: 8, flexShrink: 0 }}><button onClick={() => alert(`${doc.titre || "Document"}\n\n${doc.contenu || ""}`)} style={{ border: "1px solid #D8C9B8", background: "#FFFFFF", color: "#8B7361", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700 }}>
                      Voir
                    </button><button onClick={() => supprimerDocument(doc.id)} style={{ border: "1px solid rgba(184,92,58,0.35)", background: "rgba(184,92,58,0.08)", color: "#B85C3A", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 700 }}>
                      Supprimer
                    </button></div></div></article>
            ))}
          </div>
        )}
      </section></main>
  );
}
